import { m } from "framer-motion";
import { Icon } from "./Icon";
import { EASE } from "./Motion";

/** The track's `p-1`, in px — the pill is inset by it on every side. */
const TRACK_PADDING = 4;

export interface Segment {
  key: string;
  label: string;
}

/**
 * A row of segments with a pill that slides between them, on the same spring
 * as the attendance switch — see AttendanceToggle, which this deliberately
 * mirrors so the two controls read as the same mechanism rather than two house
 * styles.
 *
 * The pill is pale on a paler track rather than a dark slab, for the reason
 * AttendanceToggle gives: a label that has to turn light for a dark pill goes
 * briefly invisible against the track while the pill is still on its way over.
 * Keeping the pill light keeps every label dark, and the pill can then take as
 * long as it likes to arrive.
 *
 * The track carries no column gap on purpose. The pill is positioned by a
 * transform percentage, which resolves against the pill's own width, so slot n
 * sits at exactly n * 100% only while a slot and the pill are the same size —
 * true with equal columns and nothing between them, and false the moment a gap
 * is added, which leaves the pill fractionally wide and landing short. The
 * pill is the separation; it doesn't need help.
 */
export function SegmentedTabs({
  segments,
  activeKey,
  onSelect,
  enterFrom,
  variant = "full",
  tone = "paper",
  className = "",
}: {
  segments: Segment[];
  activeKey: string;
  onSelect: (key: string) => void;
  /**
   * The segment the pill should travel *from* on mount. Only meaningful where
   * the control is torn down and rebuilt between presses — tabs that navigate
   * between two routes have no memory of where the pill was, so the tab that
   * navigated says. A control that switches state in place needs none of this:
   * it keeps its own pill and simply moves it.
   */
  enterFrom?: string;
  /**
   * `full` spans its container, one equal column per segment, and marks the
   * active one with a dot — the page-level tab strip. `compact` sizes to its
   * content for a control sitting inside a row of its own, where a dot would
   * only crowd it. `subtle` is a smaller, narrower variant with reduced visual
   * prominence.
   */
  variant?: "full" | "compact" | "subtle";
  /**
   * Which room the control is standing in. `paper` is the app's bone ground;
   * `vault` is the dark finance area, where the same pale-pill-on-a-paler-
   * track construction has to be built out of translucent white instead of
   * the paper tokens — the pill is still the lighter of the two, so the
   * reason the labels never have to change colour still holds.
   */
  tone?: "paper" | "vault";
  /** Caller-owned spacing. The control has opinions about itself, not about
   * where it sits. */
  className?: string;
}) {
  const compact = variant === "compact";
  const vault = tone === "vault";
  const subtle = variant === "subtle";
  const index = segments.findIndex((s) => s.key === activeKey);
  const slot = `${index * 100}%`;
  const fromIndex = enterFrom ? segments.findIndex((s) => s.key === enterFrom) : -1;
  const travelling = fromIndex >= 0 && fromIndex !== index;

  return (
    <div
      className={`relative ${compact ? "inline-grid" : subtle ? "grid w-52 mx-auto" : "grid"} ${
        vault
          ? "p-1 bg-white/[0.045] border border-white/10"
          : subtle
            ? "p-1.5 bg-paper2/50 border border-line-soft/40"
            : "p-1 bg-paper2 border border-line-soft"
      } rounded-lg font-mono ${subtle ? "text-[8.5px]" : "text-[10.5px]"} tracking-wide font-medium uppercase ${className}`}
      // Equal columns even when the control sizes to its content: `1fr` in an
      // intrinsically sized grid resolves to the widest segment, so "DATO" gets
      // the same cell as "FLEST" — which the sliding pill require, and which
      // reads as a tidier pair besides.
      style={{ gridTemplateColumns: `repeat(${segments.length}, minmax(0, 1fr))` }}
    >
      {/* A plain conditional, not an AnimatePresence: the pill is present on
          the very first render, and AnimatePresence's `initial={false}` would
          suppress exactly the entrance and the travel-from-the-other-tab that
          are the point of it. Nothing here needs an exit — a segment always
          matches — so there is nothing for a presence wrapper to buy. */}
      {index >= 0 && (
        <m.div
          aria-hidden
          className={`absolute rounded-md ${
            vault ? "bg-white/[0.13]" : subtle ? "bg-card/30" : "bg-card"
          }`}
          style={{
            top: subtle ? "6px" : "4px",
            bottom: subtle ? "6px" : "4px",
            left: subtle ? "6px" : "4px",
            width: `calc((100% - ${subtle ? 12 : TRACK_PADDING * 2}px) / ${segments.length})`,
          }}
          // Travelling: already full-size at the segment we came from, so the
          // eye follows one object across. Arriving cold: the same
          // appear-in-place the attendance switch uses for a first answer.
          initial={
            travelling
              ? { x: `${fromIndex * 100}%`, opacity: 1, scale: 1 }
              : { x: slot, opacity: 0, scale: 0.86 }
          }
          animate={{ x: slot, opacity: 1, scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 460,
            damping: 32,
            mass: 0.85,
            opacity: { duration: 0.18, ease: EASE },
            scale: { type: "spring", stiffness: 500, damping: 24, mass: 0.8 },
          }}
        />
      )}
      {segments.map((seg) => (
        <button
          key={seg.key}
          type="button"
          aria-pressed={seg.key === activeKey}
          onClick={() => onSelect(seg.key)}
          // Relative so the labels paint over the pill sliding beneath them.
          className={`relative text-center whitespace-nowrap rounded-md flex items-center justify-center gap-1.5 transition-colors duration-200 ${
            subtle ? "py-1.5 px-1" : compact ? "py-1.5 px-3" : "py-2"
          } ${
            seg.key === activeKey
              ? vault
                ? "text-vault-ink"
                : subtle
                  ? "text-ink/75"
                  : "text-ink"
              : vault
                ? "text-vault-ink3"
                : subtle
                  ? "text-ink3/50"
                  : "text-ink3"
          }`}
        >
          {!compact && !subtle && seg.key === activeKey && <Icon name="circle" size={7} />}
          {seg.label}
        </button>
      ))}
    </div>
  );
}
